import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaCheck,
  FaClock,
  FaMapMarkerAlt,
  FaParking,
  FaRoute,
  FaShieldAlt,
  FaStar,
  FaStore,
  FaUtensils,
  FaWifi,
  FaWheelchair,
} from "react-icons/fa";
import NavBar from "../components/nav";
import Footer from "./footer";
import VisitorTips from "./VisitorTips";
import "../css/mallDescription.css";
import { notifyError, notifySuccess } from "../utils/notify";

const amenities = [
  { label: "Wheelchair accessible", icon: FaWheelchair },
  { label: "Free Wi-Fi throughout", icon: FaWifi },
  { label: "Food court & restaurants", icon: FaUtensils },
  { label: "Family-friendly facilities", icon: FaCheck },
  { label: "ATM and banking services", icon: FaCheck },
];

const MallDescription = () => {
  const navigate = useNavigate();
  const { state = {} } = useLocation();
  const [selectedImage, setSelectedImage] = useState("");

  const {
    title = "Shopping Mall",
    description = "No description available.",
    location: mallLocation = "Location not specified",
    rating = 0,
    shops = [],
    openingHours = "09:00 to 21:00",
    category = "Shopping",
  } = state;

  const gallery = useMemo(() => {
    const images = Array.isArray(state.gallery) ? state.gallery.filter(Boolean) : [];
    return [...new Set([state.image, ...images].filter(Boolean))];
  }, [state.gallery, state.image]);

  const safeRating = Math.min(5, Math.max(0, Number(rating) || 0));

  useEffect(() => {
    setSelectedImage(gallery[0] || "");
  }, [gallery]);

  const openDirections = () => {
    const destinationValue =
      mallLocation && mallLocation !== "Location not specified"
        ? mallLocation
        : title;

    if (!destinationValue || destinationValue === "Shopping Mall") {
      notifyError("Map directions are not available for this mall yet.");
      return;
    }

    const destination = encodeURIComponent(destinationValue);
    notifySuccess("Opening Maps for directions.");
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${destination}`, "_blank");
  };

  if (!state.title) {
    return (
      <div className="mall-description-page">
        <NavBar />
        <main className="mall-detail-container">
          <button className="mall-back-button" onClick={() => navigate(-1)} type="button">
            <FaArrowLeft />
            Back
          </button>
          <section className="mall-empty-state">
            <h1>Mall details unavailable</h1>
            <p>Please return to the mall list and choose a shopping destination.</p>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="mall-description-page">
      <NavBar />
      <main className="mall-detail-container">
        <header className="mall-title-row">
          <button
            className="mall-back-button icon-only"
            onClick={() => navigate(-1)}
            type="button"
          >
            <FaArrowLeft />
          </button>
          <div>
            <p className="mall-eyebrow">
              <FaStore />
              {category}
            </p>
            <h1>{title}</h1>
            <div className="mall-meta-row">
              <span>
                <FaMapMarkerAlt />
                {mallLocation}
              </span>
              <span>
                <FaStar />
                {safeRating}/5 shopping experience
              </span>
            </div>
          </div>
        </header>

        <section className="mall-hero-grid">
          <div className="mall-main-image">
            {selectedImage ? (
              <img src={selectedImage} alt={title} />
            ) : (
              <div className="mall-image-placeholder">
                <FaStore />
                <span>{category}</span>
              </div>
            )}
            <div className="mall-hero-overlay">
              <span>{openingHours}</span>
              <strong>{shops.length} stores listed</strong>
            </div>
          </div>

          <div className="mall-thumbnail-grid">
            {gallery.slice(0, 4).map((src, index) => (
              <button
                key={src}
                type="button"
                className={`mall-thumbnail ${selectedImage === src ? "active" : ""}`}
                onClick={() => setSelectedImage(src)}
              >
                <img src={src} alt={`${title} view ${index + 1}`} />
              </button>
            ))}
            {!gallery.length && (
              <div className="mall-thumbnail empty">
                <FaStore />
              </div>
            )}
          </div>
        </section>

        <section className="mall-content-grid">
          <div className="mall-main-content">
            <div className="mall-section mall-review-panel">
              <span className="mall-icon-badge primary">
                <FaStore />
              </span>
              <div>
                <p>Shopping Experience</p>
                <div className="mall-stars">
                  <strong>{safeRating}</strong>
                  {Array.from({ length: 5 }, (_, index) => (
                    <FaStar
                      key={index}
                      className={index < safeRating ? "filled" : ""}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="mall-section">
              <h2>About This Mall</h2>
              <p>{description}</p>
            </div>

            <div className="mall-section">
              <h2>Mall Information</h2>
              <div className="mall-info-grid">
                <div className="mall-info-item cyan">
                  <span>
                    <FaMapMarkerAlt />
                  </span>
                  <div>
                    <strong>Address</strong>
                    <p>{mallLocation}</p>
                  </div>
                </div>
                <div className="mall-info-item amber">
                  <span>
                    <FaClock />
                  </span>
                  <div>
                    <strong>Opening Hours</strong>
                    <p>{openingHours}</p>
                  </div>
                </div>
                <div className="mall-info-item green">
                  <span>
                    <FaStore />
                  </span>
                  <div>
                    <strong>Stores</strong>
                    <p>{shops.length} shops and boutiques</p>
                  </div>
                </div>
                <div className="mall-info-item blue">
                  <span>
                    <FaParking />
                  </span>
                  <div>
                    <strong>Parking</strong>
                    <p>Ample secure parking available</p>
                  </div>
                </div>
                <div className="mall-info-item rose">
                  <span>
                    <FaShieldAlt />
                  </span>
                  <div>
                    <strong>Security</strong>
                    <p>24/7 surveillance and security staff</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mall-section">
              <h2>Featured Stores</h2>
              {shops.length > 0 ? (
                <div className="mall-store-grid">
                  {shops.map((shop, index) => (
                    <span key={`${shop.shopName}-${index}`}>
                      <FaStore />
                      {shop.shopName}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mall-muted">No store information available yet.</p>
              )}
            </div>
          </div>

          <aside className="mall-side-panel">
            <div className="mall-directions-card">
              <span>
                <FaRoute />
              </span>
              <h2>Get Directions</h2>
              <p>Open Maps and plan your shopping trip with confidence.</p>
              <button type="button" onClick={openDirections}>
                <FaMapMarkerAlt />
                Open In Maps
              </button>
            </div>

            <div className="mall-amenities-card">
              <h2>Mall Amenities</h2>
              <div className="mall-amenities-list">
                {amenities.map((amenity) => (
                  <div key={amenity.label}>
                    <span>
                      <amenity.icon />
                    </span>
                    <p>{amenity.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </section>
      </main>
      <section className="detail-extra-section">
        <VisitorTips title={title} category="Shopping Mall" />
      </section>
      <Footer />
    </div>
  );
};

export default MallDescription;
